import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import { useCart } from '../../contexts/CartContext';
import { ArrowLeft, MessageCircle, CreditCard, DollarSign, Smartphone, Mail, Clock, MapPin, Truck, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutView = () => {
  const navigate = useNavigate();
  const { business, settings, isBusinessOpen, getTodayHours } = useBusiness();
  const { cartItems, getTotal, deliveryMode, clearCart, routePrefix } = useCart();
  const [selectedZone, setSelectedZone] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-seleccionar si hay exactamente 1 método disponible
  useEffect(() => {
    if (paymentMethods.length === 1) {
      setPaymentMethod(paymentMethods[0].id);
    }
  }, [settings]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  // Colores dinámicos
  const primaryColor = business?.primary_color || '#FFD700';
  const bgColor = business?.background_color || '#0D0D0D';
  const cardColor = business?.card_color || '#262626';
  const textColor = business?.text_color || '#FFFFFF';
  const fontHeading = business?.font_heading || 'Playfair Display, serif';
  const fontBody = business?.font_body || 'Roboto, sans-serif';

  const paymentMethods = [
    { id: 'cash', label: 'Efectivo', icon: DollarSign },
    { id: 'transfer', label: 'Transferencia', icon: CreditCard },
    { id: 'mercadopago', label: 'Mercado Pago', icon: Smartphone }
  ].filter(m => !settings?.payment_methods || settings.payment_methods.includes(m.id));

  // null = sin selección. Si hay 1 opción se auto-selecciona via useEffect.
  const [paymentMethod, setPaymentMethod] = useState(null);

  // Calcular costo de envío
  const deliveryFee = deliveryMode === 'delivery' && selectedZone ? selectedZone.fee : 0;
  const subtotal = getTotal();
  const total = subtotal + deliveryFee;

  const todayHours = getTodayHours();
  const isOpen = isBusinessOpen();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Por favor completá tu nombre, email y teléfono');
      return;
    }

    // Validar que se haya elegido método de pago (obligatorio si hay +1 opción)
    if (!paymentMethod) {
      toast.error('Por favor seleccioná un método de pago');
      return;
    }

    // Validar zona de envío obligatoria
    const activeZones = settings?.delivery_zones?.filter(z => z.active) || [];
    if (deliveryMode === 'delivery' && activeZones.length > 0 && !selectedZone) {
      toast.error('Por favor seleccioná una zona de envío');
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Por favor ingresá un email válido');
      return;
    }

    if (deliveryMode === 'delivery' && !formData.address) {
      toast.error('Por favor completá tu dirección de entrega');
      return;
    }

    setIsSubmitting(true);

    try {
      // Guardar pedido en la base de datos
      const orderData = {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        delivery_mode: deliveryMode,
        delivery_address: deliveryMode === 'delivery' ? formData.address : null,
        payment_method: paymentMethod,
        items: cartItems.map(item => ({
          product_id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          subtotal: item.price * item.quantity
        })),
        total: total,
        notes: formData.notes || null
      };

      await axios.post(`${API}/orders?slug=${business.slug}`, orderData);

      // Construir mensaje WhatsApp
      const paymentLabel = paymentMethods.find(m => m.id === paymentMethod)?.label || paymentMethod || 'No especificado';

      let message = 'Hola! Quiero hacer un pedido:\n\n';
      message += '*Nombre:* ' + formData.name + '\n';
      message += '*Email:* ' + formData.email + '\n';
      message += '*Telefono:* ' + formData.phone + '\n';
      message += '*Modalidad:* ' + (deliveryMode === 'delivery' ? 'Delivery' : 'Take Away') + '\n';
      if (deliveryMode === 'delivery') {
        if (formData.address) message += '*Direccion:* ' + formData.address + '\n';
        if (selectedZone) message += 'Zona: ' + selectedZone.name + '\n';
      }
      message += '*Forma de Pago:* ' + paymentLabel + '\n';

      message += '\n*Pedido:*\n';
      cartItems.forEach(function(item, i) {
        message += (i + 1) + '. ' + item.name + ' x' + item.quantity + ' - $' + (item.price * item.quantity) + '\n';
      });

      message += '\nEnvio: $' + deliveryFee + '\n';
      message += '*Total: $' + total + '*\n';

      if (formData.notes) {
        message += '\nNotas: ' + formData.notes + '\n';
      }

      // Encode message for URL
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/${business.whatsapp}?text=${encodedMessage}`;

      // Redirigir directamente a whatsapp (funciona en Safari)
      window.location.assign(whatsappUrl);


      // Clear cart after sending
      toast.success('¡Pedido registrado! Te redirigimos a WhatsApp');
      setTimeout(() => {
        clearCart();
      }, 1000);

    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Error al procesar el pedido. Intentá de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col pb-32" 
      style={{ backgroundColor: bgColor, color: textColor, fontFamily: fontBody }}
    >
      {/* Header */}
      <div className="sticky top-0 z-10 border-b" style={{ backgroundColor: bgColor, borderColor: '#333' }}>
        <div className="px-4 py-4">
          <div className="flex items-center justify-between max-w-md mx-auto">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(`${routePrefix}/carrito`)} className="p-2" data-testid="back-to-cart">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-bold" style={{ fontFamily: fontHeading, color: primaryColor }}>
                Finalizar Pedido
              </h2>
            </div>
            <a 
              href={routePrefix || '/'}
              className="flex items-center gap-1 text-sm opacity-60 hover:opacity-100 transition-opacity"
              data-testid="checkout-home-link"
            >
              Inicio
            </a>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 px-4 py-6 max-w-md mx-auto w-full">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Datos del Cliente */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: cardColor }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: primaryColor }}>
              Datos de Contacto
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 opacity-75">Nombre *</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Tu nombre completo"
                  required
                  className="text-white"
                  style={{ backgroundColor: bgColor, borderColor: `${primaryColor}40` }}
                  data-testid="checkout-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 opacity-75">Correo electrónico *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="tu@email.com"
                    required
                    className="text-white pl-10"
                    style={{ backgroundColor: bgColor, borderColor: `${primaryColor}40` }}
                    data-testid="checkout-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 opacity-75">Teléfono *</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Ej: 3425149602"
                  required
                  className="text-white"
                  style={{ backgroundColor: bgColor, borderColor: `${primaryColor}40` }}
                  data-testid="checkout-phone"
                />
              </div>

              {deliveryMode === 'delivery' && (
                <div>
                  <label className="block text-sm font-medium mb-2 opacity-75">Dirección de Entrega *</label>
                  <Input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Calle, número, barrio"
                    required
                    className="text-white"
                    style={{ backgroundColor: bgColor, borderColor: `${primaryColor}40` }}
                    data-testid="checkout-address"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2 opacity-75">Notas adicionales</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ej: Sin cebolla, timbre roto, etc."
                  rows={3}
                  className="text-white"
                  style={{ backgroundColor: bgColor, borderColor: `${primaryColor}40` }}
                  data-testid="checkout-notes"
                />
              </div>
            </div>
          </div>

          {/* Forma de Pago */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: cardColor }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: primaryColor }}>
              Forma de Pago
              {paymentMethods.length > 1 && !paymentMethod && (
                <span className="text-xs text-red-400 font-normal ml-2">* Obligatorio</span>
              )}
            </h3>

            {paymentMethods.length === 1 ? (
              /* 1 opción: mostrar como auto-seleccionada, sin interacción */
              <div
                className="flex items-center gap-4 p-4 rounded-xl"
                style={{ backgroundColor: `${primaryColor}20`, border: `2px solid ${primaryColor}` }}
                data-testid="payment-single-option"
              >
                {(() => { const IconComponent = paymentMethods[0].icon; return <IconComponent className="w-6 h-6" style={{ color: primaryColor }} />; })()}
                <span className="font-semibold" style={{ color: primaryColor }}>{paymentMethods[0].label}</span>
                <span className="ml-auto text-xs opacity-60">Único método disponible</span>
              </div>
            ) : (
              /* 2+ opciones: el usuario debe elegir */
              <div className="space-y-2">
                {!paymentMethod && (
                  <p className="text-xs text-red-400 mb-3" data-testid="payment-required-hint">
                    Seleccioná un método de pago para continuar
                  </p>
                )}
                <div className={`grid gap-3 ${paymentMethods.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                  {paymentMethods.map((method) => {
                    const IconComponent = method.icon;
                    const isSelected = paymentMethod === method.id;
                    return (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => setPaymentMethod(method.id)}
                        className="aspect-square rounded-xl p-4 flex flex-col items-center justify-center gap-2 transition-all hover:scale-105 active:scale-95"
                        style={{
                          backgroundColor: isSelected ? primaryColor : bgColor,
                          border: `2px solid ${isSelected ? primaryColor : primaryColor + '40'}`,
                          color: isSelected ? bgColor : textColor
                        }}
                        data-testid={`payment-${method.id}`}
                      >
                        <IconComponent className="w-8 h-8" />
                        <span className="text-xs font-semibold text-center">{method.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Zona de Envío (solo para delivery) */}
          {deliveryMode === 'delivery' && settings?.delivery_zones?.length > 0 && (
            <div className="rounded-2xl p-6" style={{ backgroundColor: cardColor }}>
              <h3 className="text-lg font-bold mb-4" style={{ color: primaryColor }}>
                <MapPin className="w-5 h-5 inline mr-2" />
                Zona de Envío
                {!selectedZone && (
                  <span className="text-xs text-red-400 font-normal ml-2">* Obligatorio</span>
                )}
              </h3>
              {!selectedZone && (
                <p className="text-xs text-red-400 mb-3" data-testid="zone-required-hint">
                  Seleccioná tu zona de envío para continuar
                </p>
              )}
              <div className="space-y-2">
                {settings.delivery_zones.filter(z => z.active).map((zone, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedZone(zone)}
                    className={`w-full p-3 rounded-xl text-left transition-all border-2`}
                    style={{
                      backgroundColor: selectedZone?.name === zone.name ? `${primaryColor}20` : bgColor,
                      borderColor: selectedZone?.name === zone.name ? primaryColor : `${primaryColor}40`,
                    }}
                    data-testid={`zone-option-${i}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{zone.name}</span>
                      <span style={{ color: primaryColor }}>
                        {zone.fee > 0 ? `+$${zone.fee}` : 'Gratis'}
                      </span>
                    </div>
                    {zone.min_order > 0 && (
                      <span className="text-xs opacity-60">Mín: ${zone.min_order}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Resumen del Pedido */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: cardColor }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: primaryColor }}>
              Resumen del Pedido
            </h3>
            <div className="space-y-3">
              {cartItems.map((item) => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span className="opacity-75">{item.name} x{item.quantity}</span>
                  <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              
              {/* Subtotal */}
              <div className="flex justify-between text-sm pt-2 border-t" style={{ borderColor: `${primaryColor}20` }}>
                <span className="opacity-75">Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              
              {/* Costo de Envío */}
              {deliveryMode === 'delivery' && (
                <div className="flex justify-between text-sm">
                  <span className="opacity-75">
                    Envío {selectedZone ? `(${selectedZone.name})` : ''}
                  </span>
                  <span style={{ color: deliveryFee > 0 ? textColor : '#22c55e' }}>
                    {deliveryFee > 0 ? `$${deliveryFee.toFixed(2)}` : 'Gratis'}
                  </span>
                </div>
              )}
              
              {/* Total */}
              <div className="border-t pt-3 mt-3" style={{ borderColor: `${primaryColor}20` }}>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">Total</span>
                  <span className="text-2xl font-black" style={{ color: primaryColor }} data-testid="checkout-total">
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Información del Negocio */}
          <div className="rounded-2xl p-4" style={{ backgroundColor: cardColor }}>
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4" style={{ color: primaryColor }} />
              <span className="text-sm font-semibold" style={{ color: primaryColor }}>Información</span>
            </div>
            <div className="space-y-2 text-xs">
              {/* Estado Abierto/Cerrado */}
              <div className="flex items-center justify-between">
                <span className="opacity-75">Estado</span>
                <span className={`px-2 py-0.5 rounded-full ${isOpen ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {isOpen ? 'Abierto ahora' : 'Cerrado'}
                </span>
              </div>
              
              {/* Horario de hoy */}
              {todayHours && (
                <div className="flex items-center justify-between">
                  <span className="opacity-75 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Horario hoy
                  </span>
                  <span>
                    {todayHours.is_open ? `${todayHours.open_time} - ${todayHours.close_time}` : 'Cerrado'}
                  </span>
                </div>
              )}
              
              {/* Tiempo estimado */}
              {deliveryMode === 'delivery' && settings?.estimated_delivery_time && (
                <div className="flex items-center justify-between">
                  <span className="opacity-75 flex items-center gap-1">
                    <Truck className="w-3 h-3" />
                    Tiempo estimado
                  </span>
                  <span>{settings.estimated_delivery_time}</span>
                </div>
              )}
              
              {/* Pedido Mínimo */}
              {settings?.min_order_amount > 0 && subtotal < settings.min_order_amount && (
                <div className="p-2 rounded-lg bg-yellow-500/20 text-yellow-400 text-center mt-2">
                  Pedido mínimo: ${settings.min_order_amount} (Faltan ${(settings.min_order_amount - subtotal).toFixed(2)})
                </div>
              )}
            </div>
          </div>
            
          {/* Mensaje informativo */}
          <div 
            className="p-4 rounded-xl text-center text-sm"
            style={{ backgroundColor: `${primaryColor}15`, border: `1px solid ${primaryColor}30` }}
            data-testid="checkout-info-message"
          >
            <p style={{ color: primaryColor }}>
              Te confirmamos el pedido por WhatsApp y te enviaremos los datos de pago.
            </p>
          </div>
        </form>
      </div>

      {/* Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 border-t px-4 py-4 shadow-lg" style={{ backgroundColor: cardColor, borderColor: '#333' }}>
        <div className="max-w-md mx-auto">
          {!paymentMethod && paymentMethods.length > 1 && (
            <p className="text-center text-xs text-red-400 mb-2" data-testid="submit-payment-warning">
              Elegí un método de pago para poder confirmar tu pedido
            </p>
          )}
          {(() => {
            const activeZones = settings?.delivery_zones?.filter(z => z.active) || [];
            const needsZone = deliveryMode === 'delivery' && activeZones.length > 0 && !selectedZone;
            return (
              <>
                {needsZone && (
                  <p className="text-center text-xs text-red-400 mb-2" data-testid="submit-zone-warning">
                    Elegí una zona de envío para continuar
                  </p>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || (!paymentMethod && paymentMethods.length > 1) || needsZone}
                  className="w-full h-16 rounded-full text-lg font-bold flex items-center justify-center gap-3 transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{ backgroundColor: '#25D366' }}
                  data-testid="send-whatsapp-button"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-6 h-6" />
                      Enviar Pedido por WhatsApp
                    </>
                  )}
                </button>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default CheckoutView;
